/**
 * Atlas PM · 操作日志 · 数据访问层
 *
 * 数据来源（真实接口，均需 audit:view 权限）：
 *   GET /api/audit/logs    全量操作流（分页 / 按动作、资源类型、操作人、时间范围过滤）
 *   GET /api/audit/verify  哈希链完整性校验（返回断链记录，空数组 = 链完好）
 *
 * ⚠️ 后端返回字段为 snake_case（直接来自 audit_log 表），此处按原样声明。
 * ⚠️ resource_type 存量数据大小写混排（task / TASK、PERMISSION / role…），
 *    后端查询已做 LOWER() 大小写不敏感匹配（P6.18），前端筛选一律传小写。
 */

import { api, qs } from '../../api/client';

/** 一条操作记录（字段名以后端返回为准） */
export interface AuditLogRecord {
  id: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_role: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  project_id: string | null;
  before_value: unknown;
  after_value: unknown;
  diff: Record<string, { from?: unknown; to?: unknown }> | null;
  reason: string | null;
  mode: string | null;
  ip: string | null;
  trace_id: string | null;
  prev_hash: string | null;
  row_hash: string | null;
  created_at: string;
}

/** 操作查询条件（均以字符串表达，便于直接拼进 query string） */
export interface AuditQueryParams {
  /** 动作（后端精确匹配，区分大小写） */
  action?: string;
  /** 资源类型（后端大小写不敏感） */
  resourceType?: string;
  /** 操作人 ID */
  actorId?: string;
  /** 起始时间（ISO 字符串） */
  from?: string;
  /** 结束时间（ISO 字符串） */
  to?: string;
  limit?: number;
  offset?: number;
}

/** GET /audit/logs 的 data 载荷 */
export interface AuditLogsResult {
  items: AuditLogRecord[];
  total: number;
}

/** 一条断链记录 */
export interface BrokenChainItem {
  brokenId: string;
  expected: string;
  actual: string;
  reason: string;
}

/** GET /audit/verify 的 data 载荷 */
export interface AuditVerifyResult {
  intact: boolean;
  brokenCount: number;
  broken: BrokenChainItem[];
}

/** 查询操作流 */
export function fetchAuditLogs(params: AuditQueryParams): Promise<AuditLogsResult> {
  return api.get<AuditLogsResult>(`/audit/logs${qs({ ...params })}`);
}

/** 校验哈希链完整性 */
export function fetchAuditVerify(): Promise<AuditVerifyResult> {
  return api.get<AuditVerifyResult>('/audit/verify');
}

/* ═══════════════════════════════════════════════════════════════
   展示用元数据（人类可读标签 + 筛选项候选）
   ═══════════════════════════════════════════════════════════════ */

/** 动作 → 中文标签 */
export const AUDIT_ACTION_LABEL: Record<string, string> = {
  TASK_CREATE: '创建任务',
  TASK_UPDATE: '更新任务',
  TASK_DELETE: '删除任务',
  TASK_SUBMIT: '提交审批',
  TASK_SUBMITTED: '提交审批',
  TASK_FROZEN: '冻结任务',
  TASK_REJECTED: '退回任务',
  TASK_UNLOCK_REQUESTED: '申请解冻',
  TASK_UNLOCKED: '解冻',
  TASK_AUTO_REFROZEN: '自动回冻',
  TASK_ARCHIVED: '归档任务',
  REFREEZE: '手动回冻',
  METRIC_CREATE: '新增指标',
  METRIC_UPDATE: '更新指标',
  METRIC_DELETE: '删除指标',
  ELEMENT_CREATE: '新增要素',
  ELEMENT_VALUE_SET: '填写要素',
  DELIVERABLE_SUBMIT: '提交产出物',
  DELIVERABLE_CONTRACT_CREATE: '新增产出物契约',
  DELIVERABLE_CONTRACT_DELETE: '删除产出物契约',
  DELIVERABLE_VERSION_CREATE: '新增产出物版本',
  PROJECT_CREATE: '创建项目',
  PROJECT_UPDATE: '更新项目',
  PROJECT_DELETE: '删除项目',
  PROJECT_TYPE_CREATE: '新增项目类型',
  PROJECT_TYPE_UPDATE: '更新项目类型',
  PROJECT_TYPE_DELETE: '删除项目类型',
  TASK_CATEGORY_CREATE: '新增任务形态',
  TASK_CATEGORY_UPDATE: '更新任务形态',
  TASK_CATEGORY_DELETE: '删除任务形态',
  APPROVAL_START: '发起审批',
  APPROVAL_APPROVE: '审批通过',
  APPROVAL_REJECT: '审批退回',
  APPROVAL_TERMINATED: '审批终止',
  APPROVAL_TIMEOUT: '审批超时',
  USER_CREATE: '新增用户',
  USER_UPDATE: '更新用户',
  USER_DISABLE: '停用用户',
  USER_ENABLE: '启用用户',
  USER_RESET_PASSWORD: '重置口令',
  ROLE_PERM_UPDATE: '调整角色权限',
  PERMISSION_DENIED: '权限拒绝',
  USER_LOGIN: '登录',
  USER_LOGOUT: '登出',
  FILE_UPLOADED: '上传文件',
  FILE_LOCKED: '文件加锁',
  FILE_MISSING_DETECTED: '检出文件缺失',
  FILE_TAMPERED_DETECTED: '检出文件被篡改',
};

/** 资源类型 → 中文标签 */
export const AUDIT_RESOURCE_LABEL: Record<string, string> = {
  task: '任务',
  project: '项目',
  approval: '审批',
  user: '用户',
  role: '角色',
  permission: '权限',
  metric: '指标',
  element: '要素',
  element_value: '要素填写值',
  project_type: '项目类型',
  task_category: '任务形态',
  deliverable: '产出物',
  deliverable_contract: '产出物契约',
};

/**
 * 资源类型筛选项（小写）。
 * ⚠️ 与后端 LOWER() 匹配一致；不含测试用类型（P69_TEST 等）。
 */
export const RESOURCE_TYPE_OPTIONS: readonly string[] = [
  'task',
  'project',
  'approval',
  'user',
  'role',
  'permission',
  'metric',
  'element',
  'element_value',
  'project_type',
  'task_category',
  'deliverable',
  'deliverable_contract',
];

/**
 * 动作筛选项。
 * ⚠️ 后端按动作精确匹配（区分大小写），故候选项一律用大写。
 */
export const ACTION_OPTIONS: readonly string[] = [
  'TASK_CREATE',
  'TASK_UPDATE',
  'TASK_DELETE',
  'TASK_SUBMIT',
  'TASK_FROZEN',
  'TASK_REJECTED',
  'TASK_UNLOCK_REQUESTED',
  'TASK_UNLOCKED',
  'TASK_AUTO_REFROZEN',
  'TASK_ARCHIVED',
  'REFREEZE',
  'METRIC_CREATE',
  'METRIC_UPDATE',
  'METRIC_DELETE',
  'ELEMENT_CREATE',
  'ELEMENT_VALUE_SET',
  'DELIVERABLE_SUBMIT',
  'DELIVERABLE_CONTRACT_CREATE',
  'PROJECT_CREATE',
  'PROJECT_UPDATE',
  'PROJECT_DELETE',
  'PROJECT_TYPE_CREATE',
  'PROJECT_TYPE_UPDATE',
  'PROJECT_TYPE_DELETE',
  'TASK_CATEGORY_CREATE',
  'TASK_CATEGORY_UPDATE',
  'TASK_CATEGORY_DELETE',
  'APPROVAL_START',
  'APPROVAL_APPROVE',
  'APPROVAL_REJECT',
  'APPROVAL_TERMINATED',
  'APPROVAL_TIMEOUT',
  'USER_CREATE',
  'USER_UPDATE',
  'USER_DISABLE',
  'USER_ENABLE',
  'USER_RESET_PASSWORD',
  'ROLE_PERM_UPDATE',
  'PERMISSION_DENIED',
  'USER_LOGIN',
  'USER_LOGOUT',
  'FILE_UPLOADED',
  'FILE_LOCKED',
];

/** 写入模式 → 中文标签 */
export const AUDIT_MODE_LABEL: Record<string, string> = {
  NORMAL: '常规',
  UNLOCK_WINDOW: '解冻窗口',
  ADMIN_OVERRIDE: '管理员绕过',
  SYSTEM: '系统自动',
};

/** 动作标签（未知动作原样返回） */
export function actionLabel(action: string): string {
  return AUDIT_ACTION_LABEL[action] ?? action;
}

/** 资源类型标签（未知类型原样返回） */
export function resourceLabel(type: string | null | undefined): string {
  if (!type) return '—';
  return AUDIT_RESOURCE_LABEL[type.toLowerCase()] ?? type;
}

/** 模式标签（空模式返回空串，不显示） */
export function modeLabel(mode: string | null | undefined): string {
  if (!mode) return '';
  return AUDIT_MODE_LABEL[mode] ?? mode;
}
